/**
 * Report-filter telemetry.
 *
 * Same registered-emitter shape as downloadCsv's, for the same reason: the
 * filter components are plain components, the reporting client is a hook, and
 * threading a callback through every report page means a report added next
 * month silently goes uncounted. ReportTelemetry registers the implementation;
 * until it does, this is a no-op.
 *
 * WHY HERE AND NOT IN useAllSelectedFilter. That hook looks like the single
 * choke point — 14 filter instances across 9 report pages — but its setSelected
 * also fires when the option list loads or refreshes, and when a deep link
 * narrows a filter to one value. Neither is a coach filtering anything, so
 * instrumenting there would emit phantom events on page load and make the
 * metric read high for reports nobody touched. A filter component's onChange is
 * unambiguously user-driven.
 *
 * `label` is the filter's visible name — "Schools", "Grade", "Date Range" — not
 * the selected values. Selections are school and section ids: high cardinality,
 * and they would land in metadata, which `reportbot.events` does not expose.
 *
 * The DEBOUNCE LIVES IN THE SUBSCRIBER, not here. useReporting's note on
 * report-filtered asks for one event per settled interaction, and checking five
 * boxes in a dropdown is one interaction; ReportTelemetry coalesces a burst into
 * a single event. Emitting per call from here is what that note warns against.
 */
export type FilterChangeEmitter = (label: string) => void;

let emitter: FilterChangeEmitter | null = null;

export const setReportFilterEmitter = (
  fn: FilterChangeEmitter | null,
): void => {
  emitter = fn;
};

/**
 * Unregister, but only if `fn` is still the registered emitter — React can
 * mount the replacement before tearing down the old instance, and an
 * unconditional clear would wipe the incoming registration. See downloadCsv's
 * clearReportDownloadEmitter, which had exactly that bug.
 */
export const clearReportFilterEmitter = (fn: FilterChangeEmitter): void => {
  if (emitter === fn) emitter = null;
};

export const notifyFilterChange = (label: string): void => {
  try {
    emitter?.(label);
  } catch {
    /* telemetry must never break a filter */
  }
};
